import { useNavigate } from "react-router-dom";
import { usePlanFeatures } from "@/hooks/use-plan-features";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Lock, ArrowLeft, Crown } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { FEATURE_UNAVAILABLE_MESSAGE } from "@/config/features";

interface FeatureBlockedPageProps {
  featureName: string;
  featureDescription?: string;
  requiredPlan?: string;
  fallbackPath?: string;
}

/**
 * Página mostrada quando o usuário tenta acessar uma funcionalidade
 * que não está incluída no seu plano.
 */
export function FeatureBlockedPage({
  featureName,
  featureDescription,
  requiredPlan = "plano superior",
  fallbackPath = "/account",
}: Readonly<FeatureBlockedPageProps>) {
  const navigate = useNavigate();
  const { subscription } = usePlanFeatures();

  const currentPlanName = subscription?.plan_name || "Seu plano atual";

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center bg-gray-50 p-4">
      <div className="max-w-md w-full text-center space-y-6">
        {/* Icon */}
        <div className="mx-auto w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center">
          <Lock className="h-10 w-10 text-amber-600" />
        </div>

        {/* Title */}
        <div className="space-y-2">
          <h1 className="text-2xl font-bold text-gray-900">
            Funcionalidade Bloqueada
          </h1>
          <p className="text-gray-600">
            A funcionalidade <strong>{featureName}</strong> não está disponível no seu plano atual.
          </p>
          {featureDescription && (
            <p className="text-sm text-gray-500">{featureDescription}</p>
          )}
        </div>

        {/* Plan Info */}
        <div className="bg-white rounded-lg border p-4 space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Plano atual:</span>
            <span className="font-medium text-gray-900">{currentPlanName}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Necessário:</span>
            <span className="font-medium text-amber-600">{requiredPlan}</span>
          </div>
        </div>

        {/* CTA */}
        <div className="space-y-3">
        <Button
          onClick={() => navigate("/account?tab=plan")}
          className="w-full"
          size="lg"
        >
          <Crown className="h-4 w-4 mr-2" />
          Atualizar meu plano
        </Button>
          
          <Button
            variant="outline"
            onClick={() => navigate(fallbackPath)}
            className="w-full"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar para {fallbackPath === "/account" ? "Minha Conta" : "Dashboard"}
          </Button>
        </div>

        {/* Help Text */}
        <p className="text-xs text-gray-500">
          Entre em contato com nosso suporte se tiver dúvidas sobre os planos.
        </p>
      </div>
    </div>
  );
}

/**
 * Componente para usar dentro de páginas para mostrar conteúdo bloqueado
 */
export function FeatureBlockedMessage({
  featureName,
}: Readonly<{
  featureName: string;
}>) {
  const navigate = useNavigate();

  return (
    <div className="min-h-[400px] flex items-center justify-center bg-gray-50 rounded-lg border-2 border-dashed">
      <div className="text-center space-y-4 p-6">
        <div className="mx-auto w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center">
          <Lock className="h-8 w-8 text-amber-600" />
        </div>
        <div className="space-y-2">
          <h3 className="font-semibold text-gray-900">
            {featureName} indisponível
          </h3>
          <p className="text-sm text-gray-600 max-w-sm">
            Esta funcionalidade não está disponível em seu plano.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate("/account?tab=plan")}
        >
          <Crown className="h-4 w-4 mr-2" />
          Ver meus planos
        </Button>
      </div>
    </div>
  );
}

/**
 * Botão "cadeado" usado para bloquear AÇÕES (ex.: Receber, Investir, Exportar,
 * Copiar dados anteriores) quando a funcionalidade não está incluída no plano.
 * Mantém o mesmo tamanho do botão original e, ao passar o mouse / clicar, apenas
 * INFORMA que o plano não inclui a funcionalidade — não redireciona para planos.
 */
export function FeatureLockButton({
  featureName,
  label,
  size = "default",
  className,
}: Readonly<{
  featureName: string;
  label: string;
  size?: "default" | "sm" | "lg" | "icon";
  className?: string;
}>) {
  return (
    <Button
      type="button"
      variant="outline"
      size={size}
      className={className}
      title={FEATURE_UNAVAILABLE_MESSAGE}
      onClick={() =>
        toast({
          variant: "destructive",
          title: "Recurso bloqueado",
          description: FEATURE_UNAVAILABLE_MESSAGE,
        })
      }
    >
      <Lock className="h-4 w-4 mr-2" />
      {label}
    </Button>
  );
}

/**
 * Card padrão de funcionalidade bloqueada pelo plano. Mantém o MESMO formato de
 * um card de gráfico/seção: o título continua visível (com cadeado) e o corpo
 * mostra a mensagem de indisponibilidade. Use como `fallback` de um FeatureGuard
 * para que o card NÃO desapareça quando a feature não está no plano.
 */
export function FeatureLockedCard({
  title,
  description,
  className = "border-border/60",
}: Readonly<{
  title: string;
  description?: string;
  className?: string;
}>) {
  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          {title}
          <Lock className="h-3 w-3 text-amber-500" />
        </CardTitle>
        {description ? <CardDescription>{description}</CardDescription> : null}
      </CardHeader>
      <CardContent>
        <FeatureBlockedMessage featureName={title} />
      </CardContent>
    </Card>
  );
}

export default FeatureBlockedPage;

